import { EpicIcons } from "@constellation-academy/epic-ui";
import * as Sentry from "@sentry/react";
import { useState, memo } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import { editLearnStartDateButton_EditLearnStartDateMutation } from "@relay/editLearnStartDateButton_EditLearnStartDateMutation.graphql";
import {
	EDIT_LEARN_START_DATE_MUTATION,
	TREE_STATE_MINIMAL_FRAGMENT,
} from "./edit-learn-start-date-button.graphql";
import {
	editButtonStyle,
	containerStyle,
	calendarWrapperStyle,
	calendarContentStyle,
	footerContainerStyle,
} from "./edit-learn-start-date-button.styles";
import { EditLearnStartDateButtonComponentProps } from "./edit-learn-start-date-button.types";
import { Button, ButtonVariant } from "../../../../components/button";

import { formatDateTime } from "../../../../components/DateTimeDisplay";
import { ConfigDatePicker } from "../../../educational-offer/parts/ConfigDatePicker";

const EditLearnStartDateButtonComponent = ({
	treeStateRef,
	userId,
	rootId,
}: EditLearnStartDateButtonComponentProps) => {
	const { t } = useTranslation("common");
	const treeState = useFragment(TREE_STATE_MINIMAL_FRAGMENT, treeStateRef ?? null);
	const [showCalendar, setShowCalendar] = useState(false);
	const [draftDate, setDraftDate] = useState<string | undefined>(treeState?.startedAt);

	const [editStartDate, isEditing] =
		useMutation<editLearnStartDateButton_EditLearnStartDateMutation>(
			EDIT_LEARN_START_DATE_MUTATION,
		);

	const handleUpdate = () => {
		if (!draftDate) return;

		editStartDate({
			variables: {
				input: {
					rootId,
					userId,
					newStartDate: draftDate,
				},
			},
			onCompleted: () => {
				toast.success(t("userEdit.startDateSuccess"));
				setShowCalendar(false);
			},
			onError: (err) => {
				toast.error(t("userEdit.startDateError"));
				Sentry.captureException(err, {
					extra: { rootId, userId, treeStateId: treeState?.id },
				});
			},
			updater: (store) => {
				const record = store.get(treeState?.id || "");
				if (record) {
					record.setValue(draftDate, "startedAt");
				}
			},
		});
	};

	return (
		<div style={containerStyle}>
			<span>{formatDateTime(treeState?.startedAt ?? "")}</span>
			<Button
				tooltip={t("userEdit.editTooltip")}
				icon={EpicIcons.PENCIL}
				variant={ButtonVariant.Textlink}
				onClick={() => setShowCalendar(!showCalendar)}
				disabled={isEditing}
				loading={isEditing}
				style={editButtonStyle}
			/>
			{showCalendar && (
				<div style={calendarWrapperStyle}>
					<div style={calendarContentStyle}>
						<ConfigDatePicker
							name="startDate"
							value={draftDate}
							onUpdate={(val) => setDraftDate(val)}
							disabled={isEditing}
							inline
							footerTemplate={() => (
								<div style={footerContainerStyle}>
									<Button
										label={t("actions.cancel")}
										variant={ButtonVariant.Error}
										onClick={() => setShowCalendar(false)}
										disabled={isEditing}
									/>
									<Button
										variant={ButtonVariant.Success}
										label={t("userEdit.update")}
										onClick={handleUpdate}
										disabled={isEditing || !draftDate}
										loading={isEditing}
									/>
								</div>
							)}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export const EditLearnStartDateButton = memo(EditLearnStartDateButtonComponent);
